import React from 'react';

export const presets = {
  id: 'compression-presets',
  title: 'Compression Presets',
  description: 'Save and reuse predefined compression settings for faster, consistent workflows.',
  category: 'settings',
  lastUpdated: '2025-07-08',
  tags: ['presets', 'compression', 'settings'],
  content: () => (
    <div className="space-y-6">
      <p className="text-muted-foreground leading-relaxed">
        Compression presets let you store a named set of compression options—like texture quality, Draco
        level, and mesh transforms—so you can apply them to any upload with a single click or API call.
      </p>

      <div>
  <h2 className="text-xl font-semibold text-foreground mb-3">Why use presets?</h2>
  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
          <li>Ensure every model is processed with identical settings.</li>
          <li>Switch between quality-focused and size-focused configurations instantly.</li>
          <li>Save time—no need to tweak advanced options for every upload.</li>
        </ul>
      </div>

      <div>
        <h2 className="text-xl font-semibold text-foreground mb-3">Creating a preset</h2>
        <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
          <li>Open the <span className="text-accent">Dashboard → Presets</span> page.</li>
          <li>Click <span className="text-accent">New Preset</span>.</li>
          <li>Give your preset a descriptive name (e.g.&nbsp;“High-fidelity Web”).</li>
          <li>Adjust the advanced options to suit your needs.</li>
          <li>Save. Optionally set the preset as <em>default</em> so it’s auto-selected next time.</li>
        </ol>
      </div>

      <div>
        <h2 className="text-xl font-semibold text-foreground mb-3">Applying presets in the UI</h2>
        <p className="text-muted-foreground leading-relaxed mb-3">
          Whenever you upload files (single or batch), you’ll see a <span className="text-accent">Preset</span>
          dropdown. Pick any of your saved presets and the advanced options panel will update instantly.
          The chosen preset ID is included with your upload so the server applies those settings.
        </p>
      </div>

      <div>
        <h2 className="text-xl font-semibold text-foreground mb-3">Using presets via the API</h2>
        <p className="text-muted-foreground leading-relaxed mb-3">
          Send the header <code className="text-accent">x-preset-id: &lt;preset_uuid&gt;</code> alongside your
          file in <code className="text-accent">/api/1/files/upload/sync</code> or
          <code className="text-accent">/api/1/files/upload/async</code> requests. The backend merges the preset
          options with any ad-hoc overrides you include in <code className="text-accent">x-compression-options</code>.
        </p>
        <pre className="bg-background-elevated p-4 rounded text-sm text-foreground overflow-x-auto"><code>curl -X POST \
  -H "Authorization: Bearer &lt;your_token&gt;" \
  -H "x-preset-id: 123e4567-e89b-12d3-a456-426614174000" \
  -F "file=@model.glb" \
  https://compress-glb.com/api/1/files/upload/sync</code></pre>
      </div>

      <div>
  <h2 className="text-xl font-semibold text-foreground mb-3">Managing presets</h2>
  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
          <li>Edit a preset at any time from the Presets page—changes apply to future uploads.</li>
          <li>Set one preset as default; it loads automatically for new sessions.</li>
          <li>Delete presets you no longer need. This does not affect jobs that already used them.</li>
        </ul>
      </div>

      <div>
        <h2 className="text-xl font-semibold text-foreground mb-3">Example presets</h2>
        <table className="w-full text-muted-foreground text-sm border-collapse">
          <thead>
            <tr>
              <th className="border-b border p-2 text-left">Name</th>
              <th className="border-b border p-2 text-left">Focus</th>
              <th className="border-b border p-2 text-left">Key Settings</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border-b border p-2">High-fidelity Web</td>
              <td className="border-b border p-2">Quality</td>
              <td className="border-b border p-2">Texture quality 90 %, Draco level 5</td>
            </tr>
            <tr>
              <td className="border-b border p-2">Ultra-small AR/VR</td>
              <td className="border-b border p-2">File size</td>
              <td className="border-b border p-2">Texture resize 512, quality 70 %, Draco level 10</td>
            </tr>
            <tr>
              <td className="p-2">No-texture Optimisation</td>
              <td className="p-2">Geometry-only</td>
              <td className="p-2">texture.enabled false, mesh transforms on</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}; 